import { useEffect, useRef } from 'react';
import Phaser from 'phaser';

import { createPhaserGameLifecycle } from '@/shared/utils/phaserGameLifecycle';
import { syncPhaserCanvasSize } from '@/shared/utils/phaserCanvasSize';
import { createGameConfig } from '../game/config';
import { EventBus, EVENTS } from '../game/events/tracingGameEventBus';

import type { RefObject } from 'react';
import type { GameFinishEvent } from '@/features/gameSession';
import type { TracingGameData } from '../types';

type UseTracingGamePhaserGameParams = {
	audioOn: boolean;
	containerRef: RefObject<HTMLDivElement | null>;
	debug: boolean;
	gameData?: TracingGameData | null;
	onStart?: () => void;
	onFailure: (onComplete: () => void) => void;
	onVictory: () => void;
	onFinish?: (event: GameFinishEvent) => void;
};

const gameLifecycle = createPhaserGameLifecycle();

export function useTracingGamePhaserGame({
	audioOn,
	containerRef,
	debug,
	gameData,
	onStart,
	onFailure,
	onVictory,
	onFinish,
}: UseTracingGamePhaserGameParams): void {
	const gameRef = useRef<Phaser.Game | null>(null);
	const audioOnRef = useRef(audioOn);
	const gameDataRef = useRef(gameData);
	const sceneReadyRef = useRef(false);

	gameDataRef.current = gameData;

	useEffect(() => {
		const handleStart = () => onStart?.();
		EventBus.on(EVENTS.GAME_STARTED, handleStart);
		EventBus.on(EVENTS.FAILURE, onFailure);
		EventBus.on(EVENTS.VICTORY, onVictory);
		const handleFinish = (event: GameFinishEvent) => onFinish?.(event);
		EventBus.on(EVENTS.GAME_FINISHED, handleFinish);
		return () => {
			EventBus.off(EVENTS.GAME_FINISHED, handleFinish);
			EventBus.off(EVENTS.FAILURE, onFailure);
			EventBus.off(EVENTS.GAME_STARTED, handleStart);
			EventBus.off(EVENTS.VICTORY, onVictory);
		};
	}, [onFailure, onFinish, onStart, onVictory]);

	useEffect(() => {
		audioOnRef.current = audioOn;
		EventBus.emit(EVENTS.SET_AUDIO_ON, audioOn);
	}, [audioOn]);

	useEffect(() => {
		if (!sceneReadyRef.current) {
			return;
		}

		EventBus.emit(EVENTS.LOAD_GAME, {
			audioOn: audioOnRef.current,
			debug,
			gameData,
		});
	}, [debug, gameData]);

	useEffect(() => {
		let isMounted = true;
		let resizeObserver: ResizeObserver | null = null;
		let handleResize: (() => void) | null = null;

		const emitGamePayload = () => {
			EventBus.emit(EVENTS.LOAD_GAME, {
				audioOn: audioOnRef.current,
				debug,
				gameData: gameDataRef.current,
			});
		};

		const handleSceneReady = () => {
			sceneReadyRef.current = true;
			emitGamePayload();
		};

		const mountGame = async () => {
			await gameLifecycle.waitForPendingDestroy();

			const container = containerRef.current;
			if (!isMounted || !container || gameRef.current) {
				return;
			}

			const game = new Phaser.Game(createGameConfig(container));
			gameRef.current = game;
			syncPhaserCanvasSize(game, container, { fallback: 'viewport' });

			handleResize = () => {
				if (gameRef.current && container.isConnected) {
					syncPhaserCanvasSize(gameRef.current, container, { fallback: 'viewport' });
				}
			};

			if (typeof ResizeObserver !== 'undefined') {
				resizeObserver = new ResizeObserver(handleResize);
				resizeObserver.observe(container);
			} else {
				window.addEventListener('resize', handleResize);
			}
		};

		EventBus.on(EVENTS.SCENE_READY, handleSceneReady);
		mountGame();

		return () => {
			isMounted = false;
			resizeObserver?.disconnect();
			if (handleResize) {
				window.removeEventListener('resize', handleResize);
			}
			EventBus.off(EVENTS.SCENE_READY, handleSceneReady);
			sceneReadyRef.current = false;

			if (gameRef.current) {
				gameLifecycle.destroy(gameRef.current);
			}

			gameRef.current = null;
		};
	}, [containerRef, debug]);
}
